// The note a value review writes for the person (ADR-015). Each decided row is
// one sentence, "Label: what was picked", in row order. The note is the
// person's: once they edit it, a later pick only replaces that row's own
// sentence and keeps every other word they wrote.
//
// rows: [{ id, label, text }] in row order; text is null for an undecided row.
// written: { id: sentence } as last written into the note. Once the note is
// the person's, a row whose answer has not changed since then is left alone,
// so a pick elsewhere never overwrites a sentence they rewrote.

export function sentence(row) {
  return row.text ? `${row.label}: ${row.text}` : null
}

function lineFor(lines, row) {
  return lines.findIndex(line => line.startsWith(`${row.label}:`))
}

// Duplicate display labels need an unambiguous note prefix. Row order is fixed
// by the review revision; ordinal prefixes keep internal row ids out of the note.
function namedRows(rows) {
  if (new Set(rows.map(row => row.label)).size === rows.length) return rows
  return rows.map((row, index) => ({ ...row, label: `Item ${index + 1} — ${row.label}` }))
}

export function composeNote(note, rows, owned, written = {}) {
  rows = namedRows(rows)
  if (!owned) return rows.map(sentence).filter(Boolean).join('\n')

  const lines = (note || '').replace(/\s+$/, '').split('\n')
  rows.forEach(row => {
    const said = sentence(row)
    if (row.id in written && written[row.id] === said) return
    const at = lineFor(lines, row)
    if (at >= 0) {
      if (said) lines[at] = said
      else lines.splice(at, 1)
    } else if (said) {
      lines.push(said)
    }
  })
  return lines.filter((line, index) => line !== '' || index > 0).join('\n').replace(/^\n+/, '')
}

export function writtenSentences(rows) {
  return Object.fromEntries(namedRows(rows).map(row => [row.id, sentence(row)]))
}

// The rows whose sentence the person changed or removed, or wrote for a row
// they left undecided. Their answers apply nothing on their own; the note
// speaks for them.
export function editedRows(note, rows) {
  rows = namedRows(rows)
  const lines = (note || '').split('\n')
  return rows.filter(row => {
    const said = sentence(row)
    const at = lineFor(lines, row)
    if (said) return at < 0 || lines[at].trim() !== said
    return at >= 0
  }).map(row => row.id)
}
